import React, { useState, useCallback, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useCurrentUser, useTaskComments, useCreateTaskComment, useDeleteTaskComment, useAllUsers } from '../hooks/useQueries';
import { formatTimestamp } from '../utils/timestampFormat';
import PopupMenu from './Popup/PopupMenu';
import Modal from './Popup/Modal';
import './Comments.css';

// Individual comment bubble component
const CommentBubble = ({ comment, currentUserName, onDelete, isViewer = false }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const canDelete = currentUserName && comment.author_name === currentUserName && !isViewer;

    // Handle link clicks with React event delegation
    const handleCommentClick = useCallback((e) => {
        if (e.target.tagName === 'A' && e.target.href) {
            e.preventDefault();
            const proceed = window.confirm('By clicking this link, you are leaving RIIMS. Continue?');
            if (proceed) {
                window.open(e.target.href, '_blank', 'noopener,noreferrer');
            }
        }
    }, []);

    const handleMenuClick = (e) => {
        const rect = e.target.getBoundingClientRect();
        setMenuPosition({
            x: rect.left + rect.width / 2,
            y: rect.bottom + 5
        });
        setShowMenu(true);
    };

    // Utility to safely linkify URLs and highlight @mentions in text
    function linkify(text) {
        if (!text) return '';

        // Highlight @mentions in format @[Name](userId)
        const withMentions = text.replace(/@\[([^\]]+)\]\((\d+)\)/g, (match, name) => {
            const escapedName = name.replace(/[<>&"']/g, (char) => {
                const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' };
                return entities[char];
            });
            return `<span style="background-color: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 12px; font-weight: 500; display: inline-block; margin: 0 2px;">@${escapedName}</span>`;
        });

        // More robust URL regex that handles edge cases better
        const urlRegex = /\b(?:https?:\/\/|www\.)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(?:\/[^\s<>"{}|\\^`[\]]*)?/gi;

        return withMentions.replace(urlRegex, (match) => {
            try {
                const original = match.trim();
                let cleanUrl = original;

                // Separate trailing punctuation likely not part of URL
                const trailingMatch = cleanUrl.match(/([.,;:!?)\]}]+)$/);
                const trailing = trailingMatch ? trailingMatch[1] : '';
                if (trailing) {
                    cleanUrl = cleanUrl.slice(0, -trailing.length);
                }

                const href = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;
                const urlObj = new URL(href);

                if (!['http:', 'https:'].includes(urlObj.protocol)) return match;
                if (!urlObj.hostname || urlObj.hostname.length < 1) return match;

                return `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailing}`;
            } catch {
                return match;
            }
        });
    }

    return (
        <div className="comment-bubble">
            <div
                className="comment-text"
                onClick={handleCommentClick}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(linkify(comment.comment_text)) }}
            />
            <div className="comment-meta">
                <span className="comment-author">{comment.author_name}</span>
                <span className="comment-date">{formatTimestamp(comment.created_at)}</span>
            </div>

            {canDelete && (
                <div className="comment-menu-button">
                    <button
                        onClick={handleMenuClick}
                        data-popup-trigger="true"
                        className="menu-btn"
                    >
                        
                    </button>
                </div>
            )}

            {showMenu && (
                <PopupMenu
                    x={menuPosition.x}
                    y={menuPosition.y}
                    onClose={() => setShowMenu(false)}
                    items={[
                        {
                            label: 'Delete Comment',
                            onClick: () => onDelete(comment),
                            color: '#dc2626'
                        }
                    ]}
                />
            )}
        </div>
    );
};

// Main TaskComments component
export default function TaskComments({ taskId, isViewer = false }) {
    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showMentionList, setShowMentionList] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const textareaRef = useRef(null);
    const mentionListRef = useRef(null);

    // Fetch current user information
    const { data: currentUser } = useCurrentUser();
    const authorName = currentUser?.name || 'Unknown User';

    // Fetch all users for mentions
    const { data: usersData = [] } = useAllUsers();
    
    const users = usersData.map(user => ({
        id: parseInt(user.id, 10), // Ensure ID is a number for comparison
        name: (user.name || user.email || `User ${user.id}`),
        email: (user.email || '')
    }));

    // Fetch comments
    const {
        data: comments = [],
        isLoading,
        error
    } = useTaskComments(taskId);

    // Mutations
    const createCommentMutation = useCreateTaskComment({
        onError: (error) => {
            setIsSubmitting(false);
            console.error('Failed to add comment:', error);
        }
    });
    
    const deleteCommentMutation = useDeleteTaskComment({
        onError: (error) => {
            setIsDeleting(false);
            console.error('Failed to delete comment:', error);
        }
    });

    const handleDeleteComment = useCallback((comment) => {
        setCommentToDelete(comment);
        setShowDeleteModal(true);
    }, []);

    const confirmDeleteComment = useCallback(() => {
        if (commentToDelete) {
            setIsDeleting(true);
            deleteCommentMutation.mutate(
                { 
                    commentId: commentToDelete.id, 
                    taskId 
                },
                {
                    onSuccess: () => {
                        setShowDeleteModal(false);
                        setCommentToDelete(null);
                        setIsDeleting(false);
                    }
                }
            );
        }
    }, [commentToDelete, deleteCommentMutation, taskId]);

    // Extract @mentions from comment text - parses @[Name](userId) format
    const extractMentions = useCallback((text) => {
        // Match mentions in format: @[Name](userId)
        const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
        const mentions = [];
        const seen = new Set(); // Prevent duplicates
        let match;
        
        while ((match = mentionRegex.exec(text)) !== null) {
            const userId = parseInt(match[2], 10);
            
            // Find the user by ID to get their email
            const user = users.find(u => u.id === userId);
            
            if (user && !seen.has(userId)) {
                seen.add(userId);
                mentions.push({
                    user_id: user.id,
                    user_email: user.email,
                    user_name: user.name
                });
            }
        }
        
        return mentions;
    }, [users]);

    const handleSubmitComment = useCallback(async (e) => {
        e.preventDefault();
        if (!newComment.trim() || isSubmitting) return;

        setIsSubmitting(true);
        const mentions = extractMentions(newComment);
        
        createCommentMutation.mutate(
            { 
                task_id: taskId, 
                comment_text: newComment.trim(), 
                author_name: authorName,
                mentioned_users: mentions
            },
            {
                onSuccess: () => {
                    setNewComment('');
                    setIsSubmitting(false);
                }
            }
        );
    }, [newComment, isSubmitting, createCommentMutation, taskId, authorName, extractMentions]);

    // Handle textarea input for @mentions
    const handleTextareaChange = (e) => {
        const value = e.target.value;
        setNewComment(value);

        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);
        const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

        if (lastAtSymbol !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
            
            // Check if there's no space after @ (still typing the mention)
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setMentionSearch(textAfterAt.toLowerCase());
                setShowMentionList(true);
                setSelectedMentionIndex(0);
            } else {
                setShowMentionList(false);
            }
        } else {
            setShowMentionList(false);
        }
    };

    // Filter users based on mention search
    const filteredUsers = users.filter(user =>
        (user.name || '').toLowerCase().includes(mentionSearch) ||
        (user.email || '').toLowerCase().includes(mentionSearch)
    ).slice(0, 10); // Limit to 10 results

    // Handle mention selection - inserts @[Name](userId) format
    const selectMention = (user) => {
        const cursorPosition = textareaRef.current.selectionStart;
        const textBeforeCursor = newComment.substring(0, cursorPosition);
        const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
        const textAfterCursor = newComment.substring(cursorPosition);

        // Insert mention in format: @[Name](userId)
        const mentionName = (user.name || '').trim() || (user.email || '').trim() || `User ${user.id}`;
        const mentionText = `@[${mentionName}](${user.id})`;
        const newText = newComment.substring(0, lastAtSymbol) + mentionText + ' ' + textAfterCursor;
        setNewComment(newText);
        setShowMentionList(false);

        // Set cursor after the mention
        setTimeout(() => {
            const newCursorPos = lastAtSymbol + mentionText.length + 1;
            textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
            textareaRef.current.focus();
        }, 0);
    };

    // Handle keyboard navigation in mention list and backspace for atomic deletion
    const handleKeyDown = (e) => {
        // Handle mention list navigation
        if (showMentionList) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex(prev => 
                    prev < filteredUsers.length - 1 ? prev + 1 : prev
                );
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
                return;
            } else if (e.key === 'Enter' && filteredUsers.length > 0) {
                e.preventDefault();
                selectMention(filteredUsers[selectedMentionIndex]);
                return;
            } else if (e.key === 'Escape') {
                setShowMentionList(false);
                return;
            }
        }

        // Handle backspace to delete entire mention atomically
        if (e.key === 'Backspace') {
            const cursorPosition = textareaRef.current.selectionStart;
            const textBeforeCursor = newComment.substring(0, cursorPosition);
            
            // Check if we're about to delete part of a mention
            // Match @[Name](userId) pattern ending at cursor
            const mentionMatch = textBeforeCursor.match(/@\[([^\]]+)\]\((\d+)\)$/);
            
            if (mentionMatch) {
                e.preventDefault();
                // Delete the entire mention
                const mentionStart = cursorPosition - mentionMatch[0].length;
                const textAfterCursor = newComment.substring(cursorPosition);
                const newText = newComment.substring(0, mentionStart) + textAfterCursor;
                setNewComment(newText);
                
                // Set cursor to where the mention started
                setTimeout(() => {
                    textareaRef.current.setSelectionRange(mentionStart, mentionStart);
                    textareaRef.current.focus();
                }, 0);
            }
        }
    };

    // Close mention list when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mentionListRef.current && !mentionListRef.current.contains(event.target) &&
                textareaRef.current && !textareaRef.current.contains(event.target)) {
                setShowMentionList(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (isLoading) {
        return (
            <div className="comments-loading">
                <div className="loading-spinner"></div>
                <div>Loading comments...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="comments-error">
                Failed to load comments: {error.message}
            </div>
        );
    }

    return (
        <div className="comments-container">
            {/* Header */}
            <div className="comments-header">
                <h6>Comments {comments.length > 0 && `(${comments.length})`}</h6>
            </div>

            {/* Add new comment form */}
            {!isViewer && (
                <div style={{ position: 'relative' }}>
                    <form onSubmit={handleSubmitComment} className="comment-form">
                        <textarea
                            ref={textareaRef}
                            rows={2}
                            placeholder="Add a comment... (Use @ to mention users)"
                            value={newComment}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                            disabled={isSubmitting}
                            className="comment-input"
                        />
                        <button
                            type="submit"
                            disabled={!newComment.trim() || isSubmitting}
                            className="comment-submit-btn"
                        >
                            {isSubmitting ? (
                                <div className="button-spinner"></div>
                            ) : (
                                'Post'
                            )}
                        </button>
                    </form>
                    
                    {/* Mention dropdown */}
                    {showMentionList && filteredUsers.length > 0 && (
                        <div
                            ref={mentionListRef}
                            className="mention-list"
                            style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: 0,
                                marginBottom: '8px',
                                width: '100%',
                                maxWidth: '300px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                zIndex: 1000
                            }}
                        >
                            {filteredUsers.map((user, index) => (
                                <div
                                    key={user.id}
                                    className={`mention-item ${index === selectedMentionIndex ? 'selected' : ''}`}
                                    onClick={() => selectMention(user)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: index === selectedMentionIndex ? '#e3f2fd' : 'transparent',
                                        borderBottom: index < filteredUsers.length - 1 ? '1px solid #e5e7eb' : 'none'
                                    }}
                                    onMouseEnter={() => setSelectedMentionIndex(index)}
                                >
                                    <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                                        {user.name || user.email || `User ${user.id}`}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                        {user.email}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Comments list */}
            <div className="comments-list">
                {comments.length === 0 ? (
                    <div className="no-comments">
                        No comments yet. Be the first to add one!
                    </div>
                ) : (
                    comments.map((comment) => (
                        <CommentBubble 
                            key={comment.id} 
                            comment={comment} 
                            currentUserName={authorName}
                            onDelete={handleDeleteComment}
                            isViewer={isViewer}
                        />
                    ))
                )}
            </div>

            {/* Delete confirmation modal */}
            <Modal
                show={showDeleteModal}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteComment}
                title="Delete Comment"
                confirmText={isDeleting ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                confirmBtnClass="btn-danger"
                loading={isDeleting}
                content={
                    <div>
                        <p style={{ marginBottom: '1rem', color: '#374151' }}>
                            Are you sure you want to delete this comment? This action cannot be undone.
                        </p>
                        {commentToDelete && (
                            <div className="delete-comment-preview">
                                "{commentToDelete.comment_text}"
                            </div>
                        )}
                    </div>
                }
            />
        </div>
    );
}
